import React, { useState, useEffect, useRef } from "react";
import { CloseIcon } from "./Icons";
import offerup from "../services/offerup";
import { marketplace } from "../services/api";

/**
 * Modal for queueing OfferUp listing jobs.
 *
 * Listings are no longer posted directly from the browser. Submitting this
 * form enqueues a job that the Mac agent picks up and executes by driving
 * the OfferUp iOS app through iPhone Mirroring. Photos come from the
 * product's saved photos, so there is no photo upload here. The worker
 * updates marketplace_listings when a job completes.
 */
// A listing whose delete/remove job completed still has a row (status
// 'deleted'/'removed'); treat it as no listing so the product can be re-listed.
const isActiveListing = (listing) =>
  !!listing && !["deleted", "removed"].includes(listing.status);

function OfferUpListingModal({
  product,
  onClose,
  onSuccess,
  existingListing = null,
}) {
  // Form state
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState(
    "Brand new. Open to offers or trades. If this listing is up, it's available.",
  );
  const [condition, setCondition] = useState("new");
  const [category, setCategory] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isLoadingListing, setIsLoadingListing] = useState(true);
  const [marketplaceListing, setMarketplaceListing] = useState(
    isActiveListing(existingListing) ? existingListing : null,
  );
  const [queuedAction, setQueuedAction] = useState(null); // 'create' | 'update' | 'delete'
  // Set when the job was handed to the browser userscript instead of the Mac
  // agent; holds the URL in case the popup blocker ate the tab we opened.
  const [browserUrl, setBrowserUrl] = useState(null);
  const mouseDownTargetRef = useRef(null);
  const formRef = useRef(null);

  // Handle modal backdrop click (only close if click started and ended on backdrop)
  const handleOverlayMouseDown = (e) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleOverlayClick = (e) => {
    if (
      e.target === e.currentTarget &&
      mouseDownTargetRef.current === e.target
    ) {
      // Don't dismiss while a job is being queued.
      if (isSubmitting) return;
      // After a job is queued, closing must notify the parent like Done does.
      if (queuedAction) {
        handleDone();
      } else {
        onClose();
      }
    }
  };

  const isEditMode = !!marketplaceListing;

  // Check for an existing listing so we know whether to queue an update
  useEffect(() => {
    const initForm = async () => {
      if (!existingListing && product?.id) {
        try {
          const response = await marketplace.getByEntityAndMarketplace(
            product.id,
            "offerup",
          );
          if (isActiveListing(response.data?.data)) {
            setMarketplaceListing(response.data.data);
          }
        } catch (err) {
          // No existing listing, that's fine
        }
      }

      setIsLoadingListing(false);
    };

    initForm();
  }, [product?.id, existingListing]);

  // Set form values based on listing or product
  useEffect(() => {
    if (isLoadingListing) return;

    if (marketplaceListing) {
      // Edit mode - use listing data
      setTitle(marketplaceListing.title || product?.name || "");
      setPrice(marketplaceListing.price?.toString() || "");
      setDescription(marketplaceListing.description || "");
      setCondition(
        marketplaceListing.condition?.toLowerCase() ||
          product?.condition ||
          "new",
      );
      setCategory(marketplaceListing.category_name || "");
    } else {
      // Create mode - use product data
      setTitle(product?.name || "");
      setPrice(
        product?.list_sale_price?.toString() ||
          product?.retail_price?.toString() ||
          "",
      );
      if (product?.description) {
        setDescription(product.description);
      }
      setCondition(product?.condition || "new");
    }
  }, [marketplaceListing, product, isLoadingListing]);

  // Helper function to round price to nearest $5 or $10
  const roundPrice = (priceValue, roundTo = 5) => {
    if (!priceValue || isNaN(priceValue)) return "";
    const num = parseFloat(priceValue);
    return (Math.round(num / roundTo) * roundTo).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (isEditMode) {
        await offerup.queueUpdate(product, {
          title,
          description,
          price: parseFloat(price),
          condition,
          category,
          externalId: marketplaceListing.external_id,
          // The stored title is what's live on OfferUp — the agent searches
          // for it even when this update renames the listing.
          findTitle: marketplaceListing.title,
        });
        setQueuedAction("update");
      } else {
        await offerup.queueCreate(product, {
          title,
          description,
          price: parseFloat(price),
          condition,
          category,
        });
        setQueuedAction("create");
      }
    } catch (err) {
      console.error("Failed to queue OfferUp job:", err);
      setError(err.message || "Failed to queue job");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Post this listing from THIS browser instead of the Mac agent.
   *
   * Enqueues the job as a `browser` job (born claimed, so the Mac daemon
   * can't also pick it up) and opens offerup.com with the job id in the URL;
   * the userscript takes it from there using the OfferUp session already in
   * this browser. The tab is opened synchronously — a window.open() after an
   * await is what popup blockers exist to stop — and pointed at the URL once
   * the id comes back.
   */
  const handlePostInBrowser = async () => {
    // type="button" skips the form's native validation — run it explicitly
    // so a job can't be enqueued with an empty price/title/category.
    if (formRef.current && !formRef.current.reportValidity()) return;
    const tab = window.open("about:blank", "_blank");
    setIsSubmitting(true);
    setError(null);
    try {
      const job = await offerup.queueCreate(product, {
        title,
        description,
        price: parseFloat(price),
        condition,
        category,
        runner: "browser",
      });
      const url = offerup.browserPostUrl(job.id);
      setBrowserUrl(url);
      if (tab) tab.location = url;
      setQueuedAction("browser");
    } catch (err) {
      console.error("Failed to queue OfferUp browser job:", err);
      if (tab) tab.close();
      setError(err.message || "Failed to queue job");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!marketplaceListing) return;

    if (
      !window.confirm(
        "Queue removal of this listing from OfferUp? The Mac agent will delete it through your iPhone.",
      )
    ) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await offerup.queueDelete(product, {
        // Anchor on the stored title — it's what's live on OfferUp and what
        // the agent searches for, regardless of any form edits.
        title: marketplaceListing.title,
        findTitle: marketplaceListing.title,
        externalId: marketplaceListing.external_id,
      });
      setQueuedAction("delete");
    } catch (err) {
      console.error("Failed to queue delete job:", err);
      setError(err.message || "Failed to queue delete job");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = () => {
    onSuccess?.();
    onClose();
  };

  const conditionOptions = [
    { value: "new", label: "New" },
    { value: "like_new", label: "Like New" },
    { value: "good", label: "Good" },
    { value: "fair", label: "Fair" },
    { value: "poor", label: "Poor" },
  ];

  if (isLoadingListing) {
    return (
      <div
        className="modal-overlay"
        onMouseDown={handleOverlayMouseDown}
        onClick={handleOverlayClick}
      >
        <div
          className="modal"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: "500px" }}
        >
          <div
            className="modal-body"
            style={{ textAlign: "center", padding: "2rem" }}
          >
            <div className="spinner" style={{ margin: "0 auto" }}></div>
            <p style={{ marginTop: "1rem", color: "var(--text-muted)" }}>
              Loading...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success screen — job is queued for the Mac agent
  if (queuedAction) {
    const queuedMessage =
      queuedAction === "browser"
        ? "Posting in the OfferUp tab that just opened — watch it fill the form."
        : queuedAction === "create"
          ? "Queued — the Mac agent will post this through your iPhone."
          : queuedAction === "update"
            ? "Queued — the Mac agent will update this listing through your iPhone."
            : "Queued — the Mac agent will remove this listing through your iPhone.";

    return (
      <div
        className="modal-overlay"
        onMouseDown={handleOverlayMouseDown}
        onClick={handleOverlayClick}
      >
        <div
          className="modal"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: "500px" }}
        >
          <div className="modal-header">
            <h2>Job Queued</h2>
            <button onClick={handleDone} className="btn btn-ghost btn-icon">
              <CloseIcon size={20} />
            </button>
          </div>
          <div className="modal-body">
            <div
              style={{
                padding: "var(--spacing-md)",
                background: "var(--success-bg, rgba(16, 185, 129, 0.1))",
                border: "1px solid var(--success, #10b981)",
                borderRadius: "var(--radius-sm)",
                color: "var(--success, #10b981)",
                fontWeight: 500,
              }}
            >
              {queuedMessage}
            </div>
            {browserUrl && (
              <a
                href={browserUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  marginTop: "var(--spacing-md)",
                  color: "var(--accent-primary)",
                  fontSize: "0.875rem",
                }}
              >
                No tab opened? Open OfferUp here →
              </a>
            )}
            <p
              style={{
                marginTop: "var(--spacing-md)",
                color: "var(--text-muted)",
                fontSize: "0.875rem",
              }}
            >
              You can check job status in Settings under OfferUp Mac Agent.
            </p>
          </div>
          <div className="modal-footer">
            <button onClick={handleDone} className="btn btn-primary">
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Listing form
  return (
    <div
      className="modal-overlay"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "500px" }}
      >
        <div className="modal-header">
          <h2>{isEditMode ? "Edit OfferUp Listing" : "List on OfferUp"}</h2>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-icon"
            disabled={isSubmitting}
          >
            <CloseIcon size={20} />
          </button>
        </div>
        <form ref={formRef} onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div
                style={{
                  padding: "var(--spacing-sm) var(--spacing-md)",
                  background: "var(--danger-bg)",
                  color: "var(--danger)",
                  borderRadius: "var(--radius-sm)",
                  marginBottom: "var(--spacing-md)",
                }}
              >
                {error}
              </div>
            )}

            <div
              style={{
                padding: "var(--spacing-sm) var(--spacing-md)",
                background: "var(--bg-secondary)",
                borderRadius: "var(--radius-sm)",
                marginBottom: "var(--spacing-md)",
                fontSize: "0.875rem",
                color: "var(--text-secondary)",
              }}
            >
              Posting runs through the Mac agent via iPhone Mirroring. Photos
              come from the product's saved photos.
            </div>

            <label>Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={100}
              disabled={isEditMode}
            />
            {isEditMode && (
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "var(--text-secondary)",
                  marginTop: "4px",
                }}
              >
                Title can't be changed after posting — the agent finds the
                listing by it.
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "var(--spacing-md)",
                marginTop: "var(--spacing-md)",
              }}
            >
              <div>
                <label>Price</label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => setPrice(roundPrice(price, 5))}
                    title="Round to nearest $5"
                    style={{
                      padding: "8px 12px",
                      background: "var(--bg-secondary)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "0.875rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    → $5
                  </button>
                  <button
                    type="button"
                    onClick={() => setPrice(roundPrice(price, 10))}
                    title="Round to nearest $10"
                    style={{
                      padding: "8px 12px",
                      background: "var(--bg-secondary)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "0.875rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    → $10
                  </button>
                </div>
              </div>
              <div>
                <label>Condition</label>
                <select
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                >
                  {conditionOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>Category</label>
                <input
                  type="text"
                  list="offerup-categories"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder={
                    isEditMode
                      ? "Leave blank to keep current"
                      : "e.g. Health & Beauty"
                  }
                  // Updates leave the listing's category untouched, so don't
                  // force re-typing it from memory.
                  required={!isEditMode}
                />
                <datalist id="offerup-categories">
                  {offerup.CATEGORIES.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
            </div>

            <label style={{ marginTop: "var(--spacing-md)" }}>
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Describe your item..."
            />

            {isEditMode && marketplaceListing?.external_url && (
              <a
                href={marketplaceListing.external_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  marginTop: "var(--spacing-md)",
                  color: "var(--accent-primary)",
                  fontSize: "0.875rem",
                }}
              >
                View on OfferUp →
              </a>
            )}
          </div>
          <div className="modal-footer">
            {isEditMode && (
              <button
                type="button"
                onClick={handleDelete}
                className="btn btn-danger"
                disabled={isSubmitting}
                style={{ marginRight: "auto" }}
              >
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            {!isEditMode && (
              <button
                type="button"
                onClick={handlePostInBrowser}
                className="btn btn-secondary"
                disabled={isSubmitting}
                title="Post from this browser using your OfferUp session — no Mac, no phone"
              >
                Post in browser
              </button>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
              style={{ background: "#00d498" }}
            >
              {isSubmitting
                ? "Queueing..."
                : isEditMode
                  ? "Queue Update"
                  : "Queue for OfferUp"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OfferUpListingModal;
